import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Building2, LogOut, Plus, Users, KeyRound, Trash2, Power, SlidersHorizontal, Receipt, RefreshCw, CalendarClock, PhoneCall, CheckCircle2, Undo2, Mail, LogIn, MessageCircle } from 'lucide-react';
import EmailConfigTab from '../components/superadmin/EmailConfigTab';
import PlanesPagosTab from '../components/superadmin/PlanesPagosTab';
import ChatTab from '../components/superadmin/ChatTab';

const ESTADO_BADGE = {
  produccion: { label: 'Producción', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  prueba: { label: 'Prueba', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  suspendida: { label: 'Suspendida', cls: 'bg-red-100 text-red-700 border-red-200' },
};
const eur = (n) => `${Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SuperAdminApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [nueva, setNueva] = useState({ nombre: '', admin_full_name: '', admin_username: '', admin_password: '', max_empleados: 0 });
  const [creando, setCreando] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [trialDias, setTrialDias] = useState(15);
  const [savingTrial, setSavingTrial] = useState(false);

  // plan dialog
  const [plan, setPlan] = useState(null); // empresa
  const [planForm, setPlanForm] = useState({ estado_cuenta: 'prueba', fin_prueba: '', precio_por_usuario: 0, max_empleados: 0 });

  // solicitudes de demo
  const [demos, setDemos] = useState(null);
  const [demosLoading, setDemosLoading] = useState(false);

  // chat: nº mensajes sin leer para badge en la pestaña
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    const cargar = () => api.get('/superadmin/chat/unread-count')
      .then((r) => setChatUnread(r.data.mensajes_no_leidos || 0)).catch(() => {});
    cargar();
    const t = setInterval(cargar, 10000);
    return () => clearInterval(t);
  }, []);

  const cargar = () => api.get('/superadmin/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  const cargarDemos = () => {
    setDemosLoading(true);
    api.get('/superadmin/demo-requests').then((r) => setDemos(r.data)).catch(() => toast.error('Error al cargar las solicitudes de demo')).finally(() => setDemosLoading(false));
  };
  const cambiarEstadoDemo = async (d) => {
    const nuevo = d.estado === 'contactado' ? 'pendiente' : 'contactado';
    try {
      await api.put(`/superadmin/demo-requests/${d.id}`, { estado: nuevo });
      setDemos((prev) => prev.map((x) => (x.id === d.id ? { ...x, estado: nuevo } : x)));
      toast.success(nuevo === 'contactado' ? 'Marcada como contactada' : 'Marcada como pendiente');
    } catch { toast.error('Error al actualizar la solicitud'); }
  };
  useEffect(() => { cargar(); }, []);

  useEffect(() => { api.get('/superadmin/config').then((r) => setTrialDias(r.data.trial_dias)).catch(() => {}); }, []);

  const guardarTrial = async () => {
    setSavingTrial(true);
    try {
      const r = await api.put('/superadmin/config', { trial_dias: parseInt(trialDias, 10) });
      setTrialDias(r.data.trial_dias);
      toast.success(`Periodo de prueba general: ${r.data.trial_dias} días`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSavingTrial(false); }
  };

  const salir = () => { logout(); navigate('/'); };

  const crear = async (e) => {
    e.preventDefault();
    setCreando(true);
    try {
      await api.post('/superadmin/empresas', nueva);
      toast.success('Empresa creada correctamente');
      setNueva({ nombre: '', admin_full_name: '', admin_username: '', admin_password: '', max_empleados: 0 });
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear empresa');
    } finally { setCreando(false); }
  };

  const toggleActiva = async (emp) => {
    try { await api.put(`/superadmin/empresas/${emp.id}`, { activa: !emp.activa }); cargar(); }
    catch { toast.error('Error al cambiar estado'); }
  };
  const eliminar = async (emp) => {
    try { await api.delete(`/superadmin/empresas/${emp.id}`); toast.success('Empresa eliminada'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };
  const impersonar = async (emp) => {
    try {
      const { data } = await api.post(`/superadmin/empresas/${emp.id}/impersonar`);
      // Guardamos el token del superadmin para poder restaurarlo al salir
      const superToken = localStorage.getItem('app_token');
      localStorage.setItem('impersonation', JSON.stringify({
        empresa_id: data.empresa_id,
        empresa_nombre: data.empresa_nombre,
        admin_full_name: data.admin_full_name,
        admin_username: data.admin_username,
        super_token: superToken,
        started_at: new Date().toISOString(),
      }));
      localStorage.setItem('app_token', data.access_token);
      toast.success(`Accediendo al panel de ${data.empresa_nombre}...`);
      // Forzamos recarga completa para que AuthContext lea el nuevo token
      window.location.href = '/admin';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo acceder al panel');
    }
  };
  const resetPassword = async () => {
    try {
      await api.put(`/superadmin/empresas/${resetTarget.id}`, { admin_password: newPass });
      toast.success('Contraseña del administrador actualizada');
      setResetTarget(null); setNewPass('');
    } catch { toast.error('Error al actualizar contraseña'); }
  };

  const abrirPlan = (emp) => {
    setPlan(emp);
    setPlanForm({
      estado_cuenta: emp.estado_cuenta || 'produccion',
      fin_prueba: emp.fin_prueba || '',
      precio_por_usuario: emp.precio_por_usuario ?? 0,
      max_empleados: emp.max_empleados ?? 0,
    });
  };
  const guardarPlan = async () => {
    try {
      await api.put(`/superadmin/empresas/${plan.id}`, {
        estado_cuenta: planForm.estado_cuenta,
        fin_prueba: planForm.estado_cuenta === 'prueba' ? planForm.fin_prueba : '',
        precio_por_usuario: parseFloat(planForm.precio_por_usuario) || 0,
        max_empleados: parseInt(planForm.max_empleados || 0, 10),
      });
      toast.success('Plan actualizado');
      setPlan(null); cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar el plan'); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="bg-zinc-900 p-2 rounded-xl"><Building2 className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="font-heading text-lg font-bold text-zinc-900">Panel Superadministrador</h1>
              <p className="text-zinc-500 text-xs">{user?.full_name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={salir} data-testid="superadmin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="empresas" className="space-y-6">
          <TabsList className="grid w-full max-w-4xl grid-cols-5">
            <TabsTrigger value="empresas" data-testid="tab-empresas"><Building2 className="h-4 w-4 mr-2" /> Empresas</TabsTrigger>
            <TabsTrigger value="planes" data-testid="tab-planes">
              <Receipt className="h-4 w-4 mr-2" /> Facturación
            </TabsTrigger>
            <TabsTrigger value="demos" data-testid="tab-demos" onClick={() => !demos && cargarDemos()}>
              <PhoneCall className="h-4 w-4 mr-2" /> Demos
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" className="relative">
              <MessageCircle className="h-4 w-4 mr-2" /> Chat
              {chatUnread > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold h-5 min-w-[20px] px-1.5" data-testid="chat-tab-badge">
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="correo" data-testid="tab-correo">
              <Mail className="h-4 w-4 mr-2" /> Correo
            </TabsTrigger>
          </TabsList>

          {/* ===== EMPRESAS ===== */}
          <TabsContent value="empresas" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
            <Card className="border-zinc-200 h-fit">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-lg"><CalendarClock className="h-5 w-5" /> Periodo de prueba</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  <Label>Días de prueba por defecto (auto-registro)</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="1" max="365" value={trialDias}
                      onChange={(e) => setTrialDias(e.target.value)} data-testid="trial-dias-input" className="w-28" />
                    <Button className="bg-zinc-900 hover:bg-zinc-800 flex-1" onClick={guardarTrial} disabled={savingTrial} data-testid="save-trial-btn">
                      {savingTrial ? 'Guardando…' : 'Guardar'}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-400">Se aplica a las empresas nuevas que se den de alta por auto-registro. No cambia las pruebas ya en curso.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 h-fit">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-lg"><Plus className="h-5 w-5" /> Nueva empresa</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={crear} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Nombre de la empresa</Label>
                    <Input data-testid="new-empresa-nombre" value={nueva.nombre} onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Límite de empleados (0 = ilimitado)</Label>
                    <Input type="number" min="0" data-testid="new-empresa-max" value={nueva.max_empleados} onChange={(e) => setNueva({ ...nueva, max_empleados: parseInt(e.target.value || '0', 10) })} />
                  </div>
                  <div className="pt-2 border-t border-zinc-100">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Administrador de la empresa</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nombre completo</Label>
                    <Input data-testid="new-empresa-admin-name" value={nueva.admin_full_name} onChange={(e) => setNueva({ ...nueva, admin_full_name: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Usuario</Label>
                    <Input data-testid="new-empresa-admin-username" value={nueva.admin_username} onChange={(e) => setNueva({ ...nueva, admin_username: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contraseña</Label>
                    <Input type="text" data-testid="new-empresa-admin-password" value={nueva.admin_password} onChange={(e) => setNueva({ ...nueva, admin_password: e.target.value })} required />
                  </div>
                  <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" disabled={creando} data-testid="create-empresa-btn">
                    {creando ? 'Creando...' : 'Crear empresa'}
                  </Button>
                  <p className="text-xs text-zinc-400">Las empresas creadas aquí entran como <b>producción</b>. Las de auto-registro entran en <b>prueba</b> ({trialDias} días).</p>
                </form>
              </CardContent>
            </Card>
            </div>

            <Card className="border-zinc-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Empresas ({empresas.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                        <th className="py-3 px-5">Empresa</th>
                        <th className="py-3 px-5">Cuenta</th>
                        <th className="py-3 px-5">Empleados</th>
                        <th className="py-3 px-5">Precio/usuario</th>
                        <th className="py-3 px-5 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empresas.map((emp) => {
                        const est = ESTADO_BADGE[emp.estado_cuenta] || ESTADO_BADGE.produccion;
                        return (
                          <tr key={emp.id} className="border-t border-zinc-100" data-testid={`empresa-row-${emp.id}`}>
                            <td className="py-3 px-5">
                              <div className="font-medium text-zinc-900">{emp.nombre}</div>
                              <div className="text-xs text-zinc-400">{emp.admin_full_name} · @{emp.admin_username}</div>
                              {emp.email && <div className="text-xs text-zinc-400">{emp.email}</div>}
                            </td>
                            <td className="py-3 px-5">
                              <Badge variant="outline" className={est.cls} data-testid={`estado-${emp.id}`}>{est.label}</Badge>
                              {emp.estado_cuenta === 'prueba' && emp.dias_prueba_restantes != null && (
                                <div className={`text-xs mt-1 ${emp.dias_prueba_restantes < 0 ? 'text-red-600' : 'text-zinc-500'}`}>
                                  {emp.dias_prueba_restantes < 0 ? 'Caducada' : `${emp.dias_prueba_restantes} días`}
                                  {emp.fin_prueba ? ` · ${new Date(emp.fin_prueba).toLocaleDateString('es-ES')}` : ''}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-5">
                              <span className="inline-flex items-center gap-1.5 text-zinc-600">
                                <Users className="h-4 w-4" /> {emp.num_empleados}{emp.max_empleados ? ` / ${emp.max_empleados}` : ''}
                              </span>
                            </td>
                            <td className="py-3 px-5 tabular-nums text-zinc-700">{eur(emp.precio_por_usuario)}</td>
                            <td className="py-3 px-5">
                              <div className="flex justify-end gap-2">
                                <Button size="icon" variant="outline" title="Acceder al panel para dar soporte" onClick={() => impersonar(emp)} data-testid={`impersonar-${emp.id}`}
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50">
                                  <LogIn className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="outline" title="Gestionar plan" onClick={() => abrirPlan(emp)} data-testid={`plan-${emp.id}`}>
                                  <SlidersHorizontal className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="outline" title="Restablecer contraseña" onClick={() => { setResetTarget(emp); setNewPass(''); }} data-testid={`reset-pass-${emp.id}`}>
                                  <KeyRound className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="outline" title={emp.activa ? 'Desactivar' : 'Activar'} onClick={() => toggleActiva(emp)}>
                                  <Power className={`h-4 w-4 ${emp.activa ? 'text-emerald-600' : 'text-zinc-400'}`} />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700" data-testid={`delete-empresa-${emp.id}`}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>¿Eliminar {emp.nombre}?</AlertDialogTitle>
                                      <AlertDialogDescription>Se borrará la empresa, su administrador, empleados y todos los fichajes. No se puede deshacer.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(emp)} data-testid={`confirm-delete-empresa-${emp.id}`}>Eliminar</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {empresas.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No hay empresas todavía.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== FACTURACIÓN (Planes + Facturas + Stripe + Códigos) ===== */}
          <TabsContent value="planes">
            <PlanesPagosTab />
          </TabsContent>

          {/* ===== SOLICITUDES DE DEMO ===== */}
          <TabsContent value="demos">
            <Card className="border-zinc-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <PhoneCall className="h-5 w-5" /> Solicitudes de demo
                  {demos && <Badge variant="outline" className="ml-1">{demos.filter((d) => d.estado !== 'contactado').length} pendientes</Badge>}
                </CardTitle>
                <Button variant="outline" size="sm" onClick={cargarDemos} disabled={demosLoading} data-testid="demos-refresh-btn">
                  <RefreshCw className={`h-4 w-4 mr-2 ${demosLoading ? 'animate-spin' : ''}`} /> Actualizar
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="demos-table">
                    <thead>
                      <tr className="text-left text-zinc-500 border-b border-zinc-200">
                        <th className="py-2 pr-4 font-medium">Fecha</th>
                        <th className="py-2 pr-4 font-medium">Nombre</th>
                        <th className="py-2 pr-4 font-medium">Empresa</th>
                        <th className="py-2 pr-4 font-medium">Teléfono</th>
                        <th className="py-2 pr-4 font-medium">Email</th>
                        <th className="py-2 pr-4 font-medium">Empleados</th>
                        <th className="py-2 pr-4 font-medium">Estado</th>
                        <th className="py-2 font-medium text-right">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demosLoading && <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Cargando…</td></tr>}
                      {!demosLoading && demos && demos.map((d) => (
                        <tr key={d.id} className="border-b border-zinc-100" data-testid={`demo-row-${d.id}`}>
                          <td className="py-3 pr-4 whitespace-nowrap text-zinc-500">{d.created_at ? new Date(d.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td className="py-3 pr-4 font-medium text-zinc-900">{d.nombre}</td>
                          <td className="py-3 pr-4">{d.empresa}</td>
                          <td className="py-3 pr-4 whitespace-nowrap"><a href={`tel:${d.telefono}`} className="text-zinc-900 hover:underline">{d.telefono}</a></td>
                          <td className="py-3 pr-4"><a href={`mailto:${d.email}`} className="text-zinc-900 hover:underline">{d.email}</a></td>
                          <td className="py-3 pr-4">{d.num_empleados || '-'}</td>
                          <td className="py-3 pr-4">
                            {d.estado === 'contactado'
                              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">Contactada</Badge>
                              : <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="outline">Pendiente</Badge>}
                          </td>
                          <td className="py-3 text-right">
                            <Button size="sm" variant={d.estado === 'contactado' ? 'outline' : 'default'} onClick={() => cambiarEstadoDemo(d)} data-testid={`demo-toggle-${d.id}`}>
                              {d.estado === 'contactado' ? <><Undo2 className="h-3.5 w-3.5 mr-1.5" /> Pendiente</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Contactada</>}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!demosLoading && demos && demos.length === 0 && (
                        <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Todavía no hay solicitudes de demo.</td></tr>
                      )}
                      {!demos && !demosLoading && (
                        <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Pulsa «Actualizar» para cargar las solicitudes.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== CHAT EN VIVO ===== */}
          <TabsContent value="chat">
            <ChatTab />
          </TabsContent>

          {/* ===== CORREO ===== */}
          <TabsContent value="correo">
            <EmailConfigTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Restablecer contraseña — {resetTarget?.nombre}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nueva contraseña para @{resetTarget?.admin_username}</Label>
            <Input data-testid="reset-pass-input" value={newPass} onChange={(e) => setNewPass(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={resetPassword} disabled={!newPass} data-testid="save-reset-pass-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan dialog */}
      <Dialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>Gestionar plan — {plan?.nombre}</DialogTitle>
            <DialogDescription>Define el estado de la cuenta, el fin de prueba y el precio por usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Estado de la cuenta</Label>
              <Select value={planForm.estado_cuenta} onValueChange={(v) => setPlanForm({ ...planForm, estado_cuenta: v })}>
                <SelectTrigger data-testid="plan-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prueba">Prueba</SelectItem>
                  <SelectItem value="produccion">Producción</SelectItem>
                  <SelectItem value="suspendida">Suspendida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {planForm.estado_cuenta === 'prueba' && (
              <div className="space-y-1.5">
                <Label>Fin de la prueba</Label>
                <Input type="date" value={planForm.fin_prueba ? planForm.fin_prueba.slice(0, 10) : ''} onChange={(e) => setPlanForm({ ...planForm, fin_prueba: e.target.value })} data-testid="plan-fin-prueba" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Precio por usuario (€)</Label>
                <Input type="number" min="0" step="0.01" value={planForm.precio_por_usuario} onChange={(e) => setPlanForm({ ...planForm, precio_por_usuario: e.target.value })} data-testid="plan-precio" />
              </div>
              <div className="space-y-1.5">
                <Label>Límite empleados (0 = ilimitado)</Label>
                <Input type="number" min="0" value={planForm.max_empleados} onChange={(e) => setPlanForm({ ...planForm, max_empleados: e.target.value })} data-testid="plan-max" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlan(null)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarPlan} data-testid="save-plan-btn">Guardar plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminApp;
